/**
 * PlanDetail — single IRA plan view.  Combines plan metadata, BeneficiaryEditor,
 * and RmdPanel.  `planNumber` is the 10-digit prefix-padded ID per Field 802.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import type { RawJxResponse } from '@retail-ops/shared-types';
import { Button, CollapsibleSection, EntityIcon, KVPair, Pill } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useApi } from './hooks/useApi';
import { BeneficiaryEditor, type BeneficiaryDraft } from './BeneficiaryEditor';
import { RmdPanel } from './RmdPanel';
import { IRA_PLAN_TYPES, type IraPlanCode } from './constants';

interface PlanSummary {
  planNumber: string;
  planCode: IraPlanCode;
  displayName: string;
  inherited: boolean;
  cifId?: string;
  ownerBirthDate?: string;
  priorYearEndBalance?: number;
  contributionYtd?: number;
}

function planCodeFromNumber(planNumber: string): IraPlanCode {
  const prefix = planNumber.slice(0, 5);
  const hit = IRA_PLAN_TYPES.find((t) => t.prefix === prefix);
  return hit?.code ?? 'TRAD';
}

function extractPlan(raw: RawJxResponse, planNumber: string): PlanSummary {
  const rec = raw as unknown as Record<string, unknown>;
  const inner = (rec['TaxPlnInqRs_MType'] ?? rec) as Record<string, unknown>;
  const planCode = planCodeFromNumber(planNumber);
  const displayName = IRA_PLAN_TYPES.find((t) => t.code === planCode)?.label ?? planCode;
  return {
    planNumber,
    planCode,
    displayName,
    inherited: planCode === 'INHERITED'
      || inner['InheritedFlag'] === 'Y'
      || inner['Inherited'] === true,
    ...(typeof inner['CifId'] === 'string' ? { cifId: inner['CifId'] } : {}),
    ...(typeof inner['OwnerBirthDate'] === 'string' ? { ownerBirthDate: inner['OwnerBirthDate'] } : {}),
    ...(typeof inner['PriorYearEndBalance'] === 'number' ? { priorYearEndBalance: inner['PriorYearEndBalance'] } : {}),
    ...(typeof inner['ContributionYtd'] === 'number' ? { contributionYtd: inner['ContributionYtd'] } : {}),
  };
}

function extractBeneficiaries(raw: RawJxResponse): BeneficiaryDraft[] {
  const rec = raw as unknown as Record<string, unknown>;
  const arr = (rec['Beneficiaries'] ?? rec['BenfArray']) as unknown;
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    .map((b, i): BeneficiaryDraft => ({
      tempId: `existing-${i}`,
      benfDesigId: (b['BenfDesigId'] === 2 ? 2 : 1) as 1 | 2,
      name: typeof b['Name'] === 'string' ? b['Name'] : '',
      relationship: typeof b['Relationship'] === 'string' ? b['Relationship'] : '',
      percent: typeof b['Percent'] === 'number' ? b['Percent'] : 0,
      ...(typeof b['Dob'] === 'string' ? { dob: b['Dob'] } : {}),
      isNew: false,
    }));
}

export function PlanDetail(): JSX.Element {
  const { planNumber } = useParams<{ planNumber: string }>();
  const navigate = useNavigate();
  const { client, makeSignal } = useApi();

  const [plan, setPlan] = useState<PlanSummary | null>(null);
  const [benefs, setBenefs] = useState<BeneficiaryDraft[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!planNumber) return;
    const signal = makeSignal();
    Promise.allSettled([
      client.jxrl.taxPlnInq({ PlanNumber: planNumber }, { signal }),
      client.jxrl.taxPlnBenfSrch({ PlanNumber: planNumber }, { signal }),
    ]).then(([inq, benf]): void => {
      if (inq.status === 'fulfilled') {
        setPlan(extractPlan(inq.value, planNumber));
      } else {
        setError(inq.reason instanceof Error ? inq.reason.message : 'TaxPlnInq failed');
      }
      if (benf.status === 'fulfilled') {
        setBenefs(extractBeneficiaries(benf.value));
      } else {
        setBenefs([]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planNumber]);

  if (!planNumber) {
    return <EmptyState title="Plan number required" message="Open an IRA plan from the RIM dashboard." />;
  }
  if (error) {
    return (
      <EmptyState
        title="Unable to load plan"
        message={error}
        action={<Button variant="ghost" onClick={() => navigate('/rim')}>← Back to RIM</Button>}
      />
    );
  }
  if (!plan || benefs === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--sp-11)' }}>
        <LoadingSpinner label="Loading IRA plan…" size="lg" />
      </div>
    );
  }

  return (
    <div className="rim-detail">
      <header className="rim-detail__header">
        <span className="rim-detail__avatar" aria-hidden>IRA</span>
        <div>
          <h1 className="rim-detail__title">{plan.displayName} IRA · {plan.planNumber}</h1>
          <div className="rim-detail__meta">
            <code style={{ fontFamily: 'var(--font-mono)' }}>{plan.planCode}</code>
            {plan.cifId && <> · CIF {plan.cifId}</>}
            {plan.inherited && (
              <>
                {' · '}
                <Pill variant="warn">Inherited</Pill>
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', alignItems: 'flex-end' }}>
          <EntityIcon type="ira" code="IR" size={24} />
          <Button variant="ghost" size="sm" onClick={() => navigate('/rim')}>← Back</Button>
        </div>
      </header>

      <CollapsibleSection title="Operational flows" iconColor="var(--ent-ira)" meta="Core Director writes">
        <div className="rim-flow__action-grid">
          <div className="rim-flow__action-card">
            <h3>Contribution</h3>
            <p>Post a Regular, Prior-year, Rollover-in, or SEP-Employer contribution. TaxPlnTranAdd.</p>
            <Button variant="primary" onClick={() => navigate(`/rim/${plan.planNumber}/contribution`)}>Start contribution</Button>
          </div>
          <div className="rim-flow__action-card">
            <h3>Recurring contribution</h3>
            <p>Schedule weekly, biweekly, twice-monthly, monthly, or quarterly contributions from ACH or another PCB account.</p>
            <Button variant="primary" onClick={() => navigate(`/rim/${plan.planNumber}/recurring-contribution`)}>Schedule recurring</Button>
          </div>
          <div className="rim-flow__action-card">
            <h3>Distribution</h3>
            <p>Gross distribution with reason code + withholding + delivery. 1099-R stamping.</p>
            <Button variant="primary" onClick={() => navigate(`/rim/${plan.planNumber}/distribution`)}>Start distribution</Button>
          </div>
          <div className="rim-flow__action-card">
            <h3>RMD withdrawal</h3>
            <p>Age-gated by <code>IraConfig</code> (rmd-start-age). Full or partial satisfaction.</p>
            <Button variant="primary" onClick={() => navigate(`/rim/${plan.planNumber}/rmd`)}>Start RMD</Button>
          </div>
          <div className="rim-flow__action-card">
            <h3>Rollover</h3>
            <p>Direct (trustee-to-trustee) or 60-day indirect. Source + receiving + tax-year stamping.</p>
            <Button variant="primary" onClick={() => navigate(`/rim/${plan.planNumber}/rollover`)}>Start rollover</Button>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Plan summary"
        iconColor="var(--ent-ira)"
        meta={plan.inherited ? 'Inherited IRA' : 'Owner plan'}
      >
        <div className="rim-kv-grid">
          <KVPair label="Plan number" value={plan.planNumber} />
          <KVPair label="Plan type" value={plan.displayName} />
          <KVPair label="Owner CIF" value={plan.cifId ?? '—'} />
          <KVPair label="Prior YE balance" value={plan.priorYearEndBalance !== undefined ? `$${plan.priorYearEndBalance.toFixed(2)}` : '—'} />
          <KVPair label="Contribution YTD" value={plan.contributionYtd !== undefined ? `$${plan.contributionYtd.toFixed(2)}` : '—'} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Beneficiaries"
        iconColor="var(--ent-ira)"
        meta={`${benefs.length} on file`}
      >
        <BeneficiaryEditor
          planNumber={plan.planNumber}
          initial={benefs}
          inherited={plan.inherited}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Required Minimum Distribution"
        iconColor="var(--pcb-amber-text)"
      >
        <RmdPanel
          planCode={plan.planCode}
          {...(plan.ownerBirthDate ? { ownerBirthDate: plan.ownerBirthDate } : {})}
          {...(plan.priorYearEndBalance !== undefined ? { priorYearEndBalance: plan.priorYearEndBalance } : {})}
        />
      </CollapsibleSection>
    </div>
  );
}
